'use client'

import { useEffect, useState, useRef } from 'react'
import { motion, useInView } from 'framer-motion'

interface SectionTransitionProps {
  children: React.ReactNode
  className?: string
  id?: string
  immediate?: boolean // 立即显示，不需要滚动触发
}

export default function SectionTransition({ children, className = '', id, immediate = false }: SectionTransitionProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })
  const [hasAnimated, setHasAnimated] = useState(false)

  useEffect(() => {
    if (isInView && !hasAnimated) {
      setHasAnimated(true)
    }
  }, [isInView, hasAnimated])

  return (
    <motion.section
      ref={ref}
      id={id}
      className={`relative ${className}`}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{
        duration: 0.8,
        ease: [0.43, 0.13, 0.23, 0.96],
        delay: 0.2
      }}
    >
      {/* 背景光效果 - 从左到右渐变光 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -left-1/4 top-0 w-1/2 h-full bg-gradient-to-r from-orange-500/15 via-red-500/10 to-purple-500/15 blur-3xl animate-pulse"></div>
        <div className="absolute left-1/4 top-0 w-1/2 h-full bg-gradient-to-r from-blue-500/10 via-green-500/8 to-cyan-500/12 blur-3xl animate-pulse"></div>
        <div className="absolute left-3/4 top-0 w-1/4 h-full bg-gradient-to-r from-yellow-500/8 via-pink-500/10 to-red-500/12 blur-3xl animate-pulse"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/3 to-transparent"></div>
      </div>
      {/* 进入视口时的波纹效果 */}
      {isInView && !hasAnimated && (
        <motion.div
          className="absolute inset-0 pointer-events-none z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-r from-orange-500/20 to-red-600/20"
            initial={{ scale: 0, opacity: 1, width: 100, height: 100 }}
            animate={{ 
              scale: [0, 3, 5], 
              opacity: [1, 0.5, 0],
              width: [100, 300, 500],
              height: [100, 300, 500]
            }}
            transition={{
              duration: 1.5,
              ease: "easeOut"
            }}
          />
        </motion.div>
      )}

      {/* 粒子效果 */}
      {isInView && (
        <ParticleField />
      )}

      <div className="relative z-20 w-full">
        {children}
      </div>
    </motion.section>
  )
}

// 粒子场组件
function ParticleField() {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 0.8,
    duration: Math.random() * 2 + 1,
    size: Math.random() * 3 + 1,
  }))

  return (
    <div className="fixed inset-0 pointer-events-none z-5">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-orange-400/60"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
          }}
          initial={{ 
            scale: 0, 
            opacity: 0,
          }}
          animate={{ 
            scale: [0, 1, 0],
            opacity: [0, 1, 0],
            x: (Math.random() - 0.5) * 100,
            y: (Math.random() - 0.5) * 100,
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            ease: "easeOut",
            repeat: Infinity,
            repeatDelay: Math.random() * 3 + 2
          }}
        />
      ))}
    </div>
  )
}